import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, Loader2,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { filterGridClassName } from '../common/uiClasses.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { useAuth } from '../../context/AuthContext.js';
import reportService from '../../services/ReportService.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import {
  filtersFromSearch,
  filtersToSearch,
  displayPaymentLabel,
  pointsMatchFocus,
  presetDateRange,
  rollUpInsightPoints,
  suggestedGrain,
  sumPointsByPeriod,
} from '../../utils/reportInsights.js';
import DashboardLineChart from '../dashboard/components/DashboardLineChart.js';
import DashboardPieChart from '../dashboard/components/DashboardPieChart.js';
import {
  HubCategoryTick,
  hubAxisMaxChars,
  hubAxisWidth,
  hubChartHeight,
  useChartBreakpoint,
} from '../dashboard/components/chartAxis.js';
import {
  CHART_TOOLTIP_WRAPPER_PROPS,
  DashboardAmountTooltip,
} from '../dashboard/components/chartTheme.js';
import {
  REPORT_DEFS,
  buildCleanParams,
  colorMap,
  getDefaultFilters,
  isReportVisible,
} from './reportConfig.js';
import { useReportFilterCatalog } from './useReportFilterCatalog.js';

const REPORT_TYPE_ALIASES = {
  dispatch: 'ml-sheet',
  ogcl: 'ml-sheet',
  ogpl: 'ml-sheet',
  'profit-loss': 'route-pl',
  pl: 'route-pl',
  delivery: 'delivery-event',
};

const MEASURE_COLORS = {
  paid: '#16a34a',
  to_pay: '#d97706',
  on_account: '#0891b2',
  credit: '#0891b2',
  balance: '#dc2626',
  collected: '#059669',
  freight: '#2563eb',
  total: '#7c3aed',
  other: '#64748b',
  commission: '#db2777',
  booking_commission: '#db2777',
  delivery_commission: '#7c3aed',
  revenue: '#2563eb',
  net: '#059669',
  gc: '#4f46e5',
  count: '#4f46e5',
  booked: '#4f46e5',
  articles: '#0f766e',
  amount: '#9333ea',
  loading: '#ea580c',
  unloading: '#ca8a04',
  handling: '#65a30d',
  gst: '#0284c7',
  tds: '#be123c',
  salary: '#a21caf',
  discount: '#e11d48',
};

const SERIES_PALETTE = [
  '#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed',
  '#0891b2', '#db2777', '#0f766e', '#ea580c', '#4f46e5',
];

function seriesColor(key, index) {
  return MEASURE_COLORS[key] || SERIES_PALETTE[index % SERIES_PALETTE.length];
}

const LINE_PRIORITY = [
  'paid', 'to_pay', 'on_account', 'credit', 'balance', 'collected',
  'revenue', 'net', 'commission', 'booking_commission', 'delivery_commission',
  'total', 'freight', 'other', 'gc', 'count', 'booked', 'articles', 'amount',
];

const chipClass = (active) => `px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 ${
  active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
}`;

function filterChips(filters) {
  return Object.entries(filters || {}).map(([key, value]) => {
    const label = key.replace(/_/g, ' ');
    if (Array.isArray(value)) {
      return `${label}: ${value.length}`;
    }
    return `${label}: ${value}`;
  });
}

function formatKpi(kpi) {
  if (kpi.format === 'count') return Number(kpi.value || 0).toLocaleString('en-IN');
  return fullCurrency(kpi.value);
}

const InsightBar = ({ rows, metrics, metric, onMetricChange, defaultKeys = [] }) => {
  const breakpoint = useChartBreakpoint();
  const paymentKeys = ['paid', 'to_pay', 'on_account', 'credit'].filter((key) => metrics.some((item) => item.key === key));
  const withValues = (keys) => keys.filter((key) => rows.some((row) => Math.abs(Number(row[key]) || 0) >= 0.005));
  const formatOf = (key) => metrics.find((item) => item.key === key)?.format || 'currency';
  const split = metric === 'split' && paymentKeys.length > 1;
  let activeKeys = withValues(split ? paymentKeys : [metric]);
  if (!activeKeys.length) {
    const preferred = withValues(defaultKeys);
    const valued = withValues(metrics.map((item) => item.key));
    const money = valued.filter((key) => formatOf(key) !== 'count');
    activeKeys = (preferred.length ? preferred : (money.length ? money : valued)).slice(0, 4);
  }
  if (!activeKeys.length) {
    activeKeys = (metrics[0] ? [metrics[0].key] : []).filter(Boolean);
  }
  const format = formatOf(activeKeys[0]);
  const chartRows = rows.map((row) => ({ ...row, name: displayPaymentLabel(row.name) }));
  const height = hubChartHeight(chartRows.length, breakpoint);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {paymentKeys.length > 1 && (
          <button type="button" className={chipClass(split)} onClick={() => onMetricChange('split')}>
            Paid / To Pay / Credit
          </button>
        )}
        {metrics.map((item) => (
          <button key={item.key} type="button" className={chipClass(metric === item.key)} onClick={() => onMetricChange(item.key)}>
            {displayPaymentLabel(item.label)}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">No rows for this filter</p>
      ) : (
        <div className="w-full overflow-x-auto" style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows} layout="vertical" margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                type="number"
                tick={{ fontSize: breakpoint === 'sm' ? 10 : 12 }}
                tickFormatter={(value) => (format === 'count' ? value : fullCurrency(value))}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={hubAxisWidth(breakpoint)}
                interval={0}
                tick={<HubCategoryTick maxChars={hubAxisMaxChars(breakpoint)} />}
              />
              <Tooltip
                {...CHART_TOOLTIP_WRAPPER_PROPS}
                content={<DashboardAmountTooltip format={format} />}
              />
              <Legend />
              {activeKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId={split ? 'a' : undefined}
                  fill={seriesColor(key, index)}
                  name={displayPaymentLabel(metrics.find((item) => item.key === key)?.label || key)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const ReportInsightsPage = () => {
  const { type: typeParam } = useParams();
  const type = REPORT_TYPE_ALIASES[typeParam] || typeParam;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const def = REPORT_DEFS[type];
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [grain, setGrain] = useState('day');
  const [grainTouched, setGrainTouched] = useState(false);
  const [breakdownId, setBreakdownId] = useState('');
  const [metric, setMetric] = useState('split');
  const [lineMode, setLineMode] = useState('all');
  const [focus, setFocus] = useState('');

  const [filters, setFilters] = useState(() => filtersFromSearch(
    searchParams,
    getDefaultFilters(def, user),
  ));
  const hasRange = Boolean(def?.defaultFilters && ('from_date' in def.defaultFilters || 'to_date' in def.defaultFilters));

  useEffect(() => {
    if (!def || !isReportVisible(def, user)) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    reportService.getReportInsights(type, buildCleanParams(filters))
      .then((res) => {
        if (cancelled) return;
        if (res?.success === false) {
          setError(res.error || 'Could not load insights');
          setPayload(null);
          return;
        }
        setPayload(res?.data || res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err.message || 'Could not load insights');
        setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [def, filters, type, user]);

  const breakdowns = payload?.breakdowns || [];
  const activeBreakdown = breakdowns.find((item) => item.id === breakdownId) || breakdowns[0];
  const points = payload?.timeline?.points || [];
  const measures = payload?.timeline?.measures || [];

  useEffect(() => {
    if (!grainTouched && points.length) setGrain(suggestedGrain(points));
  }, [grainTouched, points]);

  useEffect(() => {
    setGrainTouched(false);
  }, [filters.from_date, filters.to_date, filters.report_date]);

  const focusedNames = activeBreakdown?.rows?.map((row) => row.name).filter((name) => name && !name.startsWith('Other (')) || [];
  const focusedRows = (activeBreakdown?.rows || []).filter((row) => !focus || row.name === focus);
  const focusedPoints = points.filter((point) => pointsMatchFocus(point, focus));
  const trendPoints = sumPointsByPeriod(rollUpInsightPoints(focus ? focusedPoints : points, grain));
  const linePool = lineMode === 'all'
    ? (() => {
      const money = measures.filter((item) => item.format !== 'count');
      return money.length ? money : measures;
    })()
    : measures;
  const preferredLines = LINE_PRIORITY.filter((key) => linePool.some((item) => item.key === key));
  const lineKeys = lineMode === 'all'
    ? (preferredLines.length ? preferredLines : linePool.map((item) => item.key)).slice(0, 4)
    : [lineMode];
  const visibleLineKeys = lineKeys.length ? lineKeys : measures.map((item) => item.key).slice(0, 4);

  useEffect(() => {
    setFilters(filtersFromSearch(searchParams, getDefaultFilters(def, user)));
    setGrainTouched(false);
    setBreakdownId('');
    setMetric('split');
    setLineMode('all');
    setFocus('');
    // Search params are read once per report so later filter edits are not overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    const next = filtersToSearch(buildCleanParams(filters));
    const current = searchParams.toString();
    if (next === current) return;
    const normalize = (value) => {
      const entries = [];
      new URLSearchParams(value).forEach((item, key) => entries.push(`${key}=${item}`));
      return entries.sort().join('&');
    };
    if (normalize(next) !== normalize(current)) setSearchParams(next, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  const filterFields = useReportFilterCatalog({ type, user, filters, setFilters });
  const activePreset = ['today', 'week', 'month', 'last_month'].find((id) => {
    const range = presetDateRange(id);
    return filters.from_date === range.from_date && filters.to_date === range.to_date;
  }) || '';

  const setRange = (next) => setFilters((prev) => ({ ...prev, ...next }));

  if (!def) {
    return (
      <DashboardLayout>
        <p className="text-sm text-slate-600">Unknown report.</p>
      </DashboardLayout>
    );
  }
  if (!isReportVisible(def, user)) {
    return (
      <DashboardLayout>
        <p className="text-sm text-slate-600">You do not have access to this report.</p>
      </DashboardLayout>
    );
  }

  const tone = colorMap[def.color] || colorMap.indigo;
  const Icon = def.icon;
  const kpiPayment = (payload?.kpis || []).flatMap((kpi) => {
    const names = {
      paid: 'Paid',
      paid_amount: 'Paid',
      to_pay: 'To Pay',
      to_pay_amount: 'To Pay',
      on_account: 'Credit',
      on_account_amount: 'Credit',
      credit: 'Credit',
      credit_amount: 'Credit',
    };
    if (!names[kpi.key] || !Number(kpi.value)) return [];
    return [{
      key: String(kpi.key).replace(/_amount$/, ''),
      name: names[kpi.key],
      value: Number(kpi.value),
    }];
  });
  const payment = (payload?.payment || []).length ? payload.payment : kpiPayment;
  const composition = payload?.composition || [];
  const share = payload?.share || [];
  const status = payload?.status || [];
  const pie = payment.length
    ? { title: 'Paid, To Pay and Credit', rows: payment, format: 'currency' }
    : composition.length
      ? { title: 'Commission split', rows: composition, format: 'currency' }
      : share.length
        ? { title: 'Share of total', rows: share, format: 'currency' }
        : { title: 'Status', rows: status, format: 'count' };
  const backQuery = filtersToSearch(buildCleanParams(filters));

  return (
    <DashboardLayout>
      <div className="w-full max-w-full min-w-0">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3 sm:mb-4">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <div
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: tone.iconBg, border: `1px solid ${tone.buttonBorder}` }}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: tone.iconColor }} />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-extrabold text-slate-900 m-0 leading-tight">{def.title} insights</h1>
              <p className="text-[11px] text-slate-400 mt-0.5 m-0">Same filters and figures as this report. Nothing here is sample data.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(backQuery ? `/reports/${type}?${backQuery}` : `/reports/${type}`)}
            className="inline-flex items-center gap-1.5 self-start border border-slate-200 bg-white rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-600"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to report
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-800">Filters</span>
          </div>
          {hasRange && (
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
              {[
                ['today', 'Today'],
                ['week', 'This week'],
                ['month', 'This month'],
                ['last_month', 'Last month'],
              ].map(([id, label]) => (
                <button key={id} type="button" className={chipClass(activePreset === id)} onClick={() => setRange(presetDateRange(id))}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className={`${filterGridClassName} [&>*]:min-w-0`}>
            {filterFields}
          </div>
          {filterChips(payload?.filters).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {filterChips(payload?.filters).map((chip) => (
                <span key={chip} className="rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-600">{chip}</span>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-[10px] p-3 mb-4 text-xs sm:text-[13px]">{error}</div>
        )}
        {payload?.warning && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{payload.warning}</div>
        )}
        {loading && (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 py-16">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading insights…
          </div>
        )}

        {!loading && payload && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
              {(payload.kpis || []).map((kpi) => (
                <div key={kpi.key} className="bg-white rounded-xl border border-gray-100 px-3 py-3 min-w-0">
                  <p className="text-[11px] text-slate-500 m-0 truncate">{displayPaymentLabel(kpi.label)}</p>
                  <p className="text-sm sm:text-base font-bold text-slate-900 m-0 mt-1 break-words">{formatKpi(kpi)}</p>
                </div>
              ))}
              <div className="bg-white rounded-xl border border-gray-100 px-3 py-3 min-w-0">
                <p className="text-[11px] text-slate-500 m-0">Records</p>
                <p className="text-sm sm:text-base font-bold text-slate-900 m-0 mt-1">{Number(payload.record_count || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 items-start">
              <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 min-w-0">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-800 m-0">Trend</h2>
                  <div className="flex gap-1.5 overflow-x-auto">
                    {['day', 'week', 'month'].map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={chipClass(grain === id)}
                        onClick={() => { setGrainTouched(true); setGrain(id); }}
                      >
                        {id[0].toUpperCase() + id.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                {measures.length > 1 && (
                  <div className="flex gap-1.5 overflow-x-auto mb-3">
                    <button type="button" className={chipClass(lineMode === 'all')} onClick={() => setLineMode('all')}>All</button>
                    {measures.map((item) => (
                      <button key={item.key} type="button" className={chipClass(lineMode === item.key)} onClick={() => setLineMode(item.key)}>
                        {displayPaymentLabel(item.label)}
                      </button>
                    ))}
                  </div>
                )}
                <DashboardLineChart
                  chartTrend={{ bucket: grain, points: trendPoints }}
                  lines={visibleLineKeys.map((key, index) => {
                    const measure = measures.find((item) => item.key === key);
                    return {
                      dataKey: key,
                      name: displayPaymentLabel(measure?.label || key),
                      color: seriesColor(key, index),
                    };
                  })}
                  format={measures.find((item) => item.key === visibleLineKeys[0])?.format || 'currency'}
                  emptyLabel={focus ? 'No dated rows for this selection' : (payload?.trend_note || 'No dated rows in this report')}
                />
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 min-w-0">
                <DashboardPieChart
                  title={pie.title}
                  rows={(pie.rows || []).map((row) => ({ ...row, name: displayPaymentLabel(row.name) }))}
                  format={pie.format}
                  emptyLabel="No split in this report"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 mb-4 min-w-0">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-800 m-0">
                  {activeBreakdown ? `By ${activeBreakdown.label}` : 'Breakdown'}
                </h2>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  {breakdowns.length > 1 && (
                    <div className="flex gap-1.5 overflow-x-auto">
                      {breakdowns.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={chipClass((activeBreakdown?.id || '') === item.id)}
                          onClick={() => { setBreakdownId(item.id); setFocus(''); setMetric('split'); }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {focusedNames.length > 1 && (
                    <select
                      value={focus}
                      onChange={(event) => setFocus(event.target.value)}
                      className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-700 bg-white max-w-full"
                    >
                      <option value="">All</option>
                      {focusedNames.map((name) => (
                        <option key={name} value={name}>{displayPaymentLabel(name)}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              {activeBreakdown ? (
                <InsightBar
                  rows={focusedRows}
                  metrics={activeBreakdown.metrics || []}
                  metric={metric}
                  onMetricChange={setMetric}
                  defaultKeys={activeBreakdown.default_keys || []}
                />
              ) : (
                <p className="text-sm text-gray-400 text-center py-10">This report has no grouped rows to chart.</p>
              )}
            </div>

            {payment.length > 0 && status.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 mb-4">
                <DashboardPieChart
                  title="Status"
                  rows={(status || []).map((row) => ({ ...row, name: displayPaymentLabel(row.name) }))}
                  format="count"
                />
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportInsightsPage;
